import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  FaSearch,
  FaUserMd,
  FaChevronRight,
  FaArrowLeft,
  FaTimes,
  FaHospital,
} from "react-icons/fa";
import axios from "../../api/axios";
import { useAuth } from "../../contexts/AuthContext";
import BottomNav from "../../components/BottomNav";
import Loading from "../../components/Loading";
import { imageUrl } from "../../utils/assets";

interface Departemen {
  practitioner_role_id: number;
  departemen_name: string;
  departemen_id: string;
}

interface Doctor {
  id: number;
  name: string;
  avatar_url: string;
  description: string;
  rating: string;
  specialities: { code: string; name: string }[];
  organizations: { code: string; name: string }[];
  departemens?: Departemen[];
  gender: string;
  count_participant: number;
  active: number;
}

interface LocationState {
  providerCode?: string;
  specialityCode?: string;
  specialityName?: string;
  polyCode?: string;
  polyName?: string;
  sickLeaveDate?: string;
}

const DoctorList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { referenceId } = useParams<{ referenceId: string }>();
  const state = (location.state || {}) as LocationState;

  const { token: authToken } = useAuth();
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const loaderRef = useRef<HTMLDivElement | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [showPolyModal, setShowPolyModal] = useState(false);

  const handleSearchChange = (value: string) => {
    setInputValue(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (value === "") {
      setSearchQuery("");
      return;
    }
    if (value.length < 3) return;
    debounceTimer.current = setTimeout(() => {
      setSearchQuery(value);
    }, 500);
  };

  const clearSearch = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setInputValue("");
    setSearchQuery("");
  };

  const fetchDoctors = async (pageNum = 1, reset = false) => {
    if (reset) setIsLoading(true);
    else setIsFetchingMore(true);
    try {
      const params: Record<string, any> = {
        page: pageNum,
        organization: state.providerCode,
        doctor: searchQuery || undefined,
      };

      if (state.specialityCode) {
        params.speciality = state.specialityCode;
      } else if (state.polyCode) {
        params.department = state.polyCode;
      }

      const res = await axios.get("primaya/doctors", {
        params,
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = res.data;
      let fetchedDoctors: Doctor[] = data.data || [];

      if (state.specialityCode) {
        fetchedDoctors = fetchedDoctors.filter((doc) =>
          doc.specialities?.some(
            (s) => s.code === state.specialityCode || s.name === state.specialityName,
          ),
        );
      }

      setDoctors((prev) => (reset ? fetchedDoctors : [...prev, ...fetchedDoctors]));
      setLastPage(data.last_page);
      setPage(pageNum);
    } catch (err) {
      console.error("Error fetching doctors:", err);
    } finally {
      setIsLoading(false);
      setIsFetchingMore(false);
    }
  };

  useEffect(() => {
    fetchDoctors(1, true);
  }, [searchQuery]);

  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && page < lastPage && !isFetchingMore) {
          fetchDoctors(page + 1);
        }
      },
      { threshold: 1 },
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [page, lastPage, isFetchingMore]);

  const handleSelectDoctor = (doctor: Doctor) => {
    if (doctor.departemens && doctor.departemens.length > 1) {
      setSelectedDoctor(doctor);
      setShowPolyModal(true);
    } else {
      navigateToSchedule(doctor, doctor.departemens?.[0]);
    }
  };

  const handlePolySelect = (poly: Departemen) => {
    setShowPolyModal(false);
    if (selectedDoctor) {
      navigateToSchedule(selectedDoctor, poly);
    }
  };

  const navigateToSchedule = (doctor: Doctor, poly?: Departemen) => {
    navigate(`/reference/${referenceId}/booking/schedule`, {
      state: {
        doctor,
        practitionerRoleId: poly?.practitioner_role_id ?? doctor.id,
        polyCode: poly?.departemen_id || state.polyCode,
        polyName: poly?.departemen_name || state.polyName,
        sickLeaveDate: state.sickLeaveDate,
      },
    });
  };

  const isTypingShort = inputValue.length > 0 && inputValue.length < 3;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="bg-teal-600 text-white pt-4 pb-6 px-4 shadow-md">
        <div className="flex items-center mb-3">
          <button
            onClick={() => navigate(-1)}
            className="mr-3 p-1 rounded-full hover:bg-teal-500 transition"
          >
            <FaArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-lg font-bold leading-tight">Pilih Dokter</h1>
            {state.specialityName && (
              <p className="text-teal-100 text-xs">Spesialisasi: {state.specialityName}</p>
            )}
          </div>
        </div>

        <div className="relative">
          <FaSearch
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={14}
          />
          <input
            type="text"
            placeholder="Cari nama dokter (min. 3 huruf)..."
            value={inputValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-9 pr-9 py-2.5 rounded-xl text-gray-800 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
          />
          {inputValue && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <FaTimes size={13} />
            </button>
          )}
        </div>

        {isTypingShort && (
          <p className="text-teal-200 text-xs mt-2 pl-1">
            Ketik {3 - inputValue.length} huruf lagi untuk mencari...
          </p>
        )}
      </div>

      <div className="flex-1 px-4 py-4 pb-24 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center items-center mt-20">
            <Loading />
          </div>
        ) : doctors.length === 0 ? (
          <div className="text-center mt-20 text-gray-400">
            <FaUserMd size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">
              {searchQuery
                ? `Dokter "${searchQuery}" tidak ditemukan`
                : "Tidak ada dokter ditemukan"}
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-400 mb-3">
              Menampilkan {doctors.length} dokter
              {state.specialityName && (
                <span> untuk spesialisasi "{state.specialityName}"</span>
              )}
            </p>
            <div className="space-y-3">
              {doctors.map((doctor) => (
                <DoctorCard
                  key={doctor.id}
                  doctor={doctor}
                  onSelect={() => handleSelectDoctor(doctor)}
                />
              ))}
            </div>
            <div
              ref={loaderRef}
              className="h-10 flex items-center justify-center mt-2"
            >
              {isFetchingMore && (
                <div className="w-5 h-5 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
              )}
            </div>
          </>
        )}
      </div>

      {showPolyModal && selectedDoctor && (
        <div className="fixed inset-0 z-[1100] flex items-end justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowPolyModal(false)}
          />
          <div className="relative bg-white w-full max-w-lg rounded-t-3xl shadow-xl animate-slide-up pb-8">
            <div className="flex items-center justify-between px-5 pt-4 pb-2 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">Pilih Poli</h3>
              <button
                onClick={() => setShowPolyModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400"
              >
                <FaTimes size={14} />
              </button>
            </div>

            <div className="px-5 pt-3 pb-2">
              <div className="flex items-center gap-3 mb-3">
                <img
                  src={imageUrl(selectedDoctor.avatar_url)}
                  alt={selectedDoctor.name}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedDoctor.name)}&background=0d9488&color=fff&size=64`;
                  }}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold text-sm text-gray-800">{selectedDoctor.name}</p>
                  <p className="text-xs text-gray-400">
                    {selectedDoctor.specialities?.[0]?.name || "Dokter"}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-5 space-y-2 max-h-60 overflow-y-auto">
              {selectedDoctor.departemens?.map((dep, idx) => (
                <button
                  key={idx}
                  onClick={() => handlePolySelect(dep)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-teal-50 hover:border-teal-200 transition-all text-left"
                >
                  <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                    <FaHospital className="text-teal-500" size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-700">{dep.departemen_name}</p>
                  </div>
                  <FaChevronRight className="text-gray-300 shrink-0" size={12} />
                </button>
              ))}
              {(!selectedDoctor.departemens || selectedDoctor.departemens.length === 0) && (
                <p className="text-sm text-gray-400 text-center py-4">
                  Tidak ada poli tersedia
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

const DoctorCard: React.FC<{ doctor: Doctor; onSelect: () => void }> = ({
  doctor,
  onSelect,
}) => {
  const speciality = doctor.specialities?.[0]?.name || "Dokter Umum";
  const hospital = doctor.organizations?.[0]?.name || "";
  const polyCount = doctor.departemens?.length || 0;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
      <div className="flex items-start p-4 gap-3">
        <div className="relative shrink-0">
          <img
            src={imageUrl(doctor.avatar_url)}
            alt={doctor.name}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                `https://ui-avatars.com/api/?name=${encodeURIComponent(doctor.name)}&background=0d9488&color=fff&size=64`;
            }}
            className="w-16 h-16 rounded-xl object-cover bg-teal-50"
          />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-800 text-sm leading-tight truncate">
            {doctor.name}
          </h3>
          <span className="inline-block mt-1 text-xs bg-teal-50 text-teal-700 font-medium px-2 py-0.5 rounded-full">
            {speciality}
          </span>
          {hospital && (
            <p className="text-xs text-gray-400 mt-1 truncate">{hospital}</p>
          )}
          {polyCount > 0 && (
            <p className="text-xs text-gray-400 mt-0.5">
              {polyCount} poli tersedia
            </p>
          )}
        </div>

        <button
          onClick={onSelect}
          className="shrink-0 self-center bg-teal-500 hover:bg-teal-600 text-white text-xs font-medium px-3 py-2 rounded-xl flex items-center gap-1.5 transition-colors"
        >
          Pilih
          <FaChevronRight size={10} />
        </button>
      </div>
    </div>
  );
};

export default DoctorList;
